/**
 * UTILIDAD VS FACTURACIÓN — la gráfica que contesta la pregunta importante.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ DOS LÍNEAS Y NO DOS GRÁFICAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * «Vendí 47.000» no dice nada solo. La pregunta de un dueño es «de lo que
 * vendí, ¿cuánto me quedó?», y eso es una distancia entre dos números — no dos
 * números.
 *
 * Por eso van en el mismo dibujo y con la franja de en medio pintada: **esa
 * franja es lo que te costó la mercancía**. Cuando se ensancha, el negocio
 * está vendiendo más barato o comprando más caro, y se ve sin leer una cifra.
 *
 * Dos gráficas separadas obligarían a comparar de memoria, saltando la vista
 * de una a otra. Es exactamente lo que el ojo no sabe hacer.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LA ESCALA ARRANCA EN CERO, SIEMPRE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Es la trampa clásica de los gráficos: recortar el eje para que las
 * diferencias parezcan enormes. Aquí eso sería mentirle al dueño sobre su
 * propio negocio — una caída del 2 % se vería como un desplome.
 *
 * Y si hay días con pérdida, el suelo baja hasta el mínimo real: una utilidad
 * negativa tiene que verse POR DEBAJO de la línea del cero, no aplastada
 * contra ella.
 */

import { useId, useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import { Tarjeta, TituloSeccion, cx } from "@/components/ui";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";

export type DiaDeGanancia = {
  fecha: string;
  ingreso: string;
  costo: string;
  ganancia: string;
};

/** Márgenes internos del dibujo, en unidades del `viewBox`. */
const ANCHO = 600;
const ALTO = 180;
const IZQ = 4;
const DER = 4;
const ARRIBA = 8;
const ABAJO = 8;

export function UtilidadVsFacturacion({
  dias,
  formato,
}: {
  dias: DiaDeGanancia[];
  formato: FormatoMoneda;
}) {
  const idGradiente = useId();
  const [encima, setEncima] = useState<number | null>(null);

  const calculado = useMemo(() => {
    const todos = dias.map((d) => ({
      fecha: d.fecha,
      ingreso: toNumber(d.ingreso),
      ganancia: toNumber(d.ganancia),
    }));

    /*
      ⚠️ SE TIRAN LOS DÍAS VACÍOS DEL PRINCIPIO.

      El rango es de treinta días siempre. Un negocio que abrió hace dos
      semanas veía dieciséis días pegados al suelo y luego la línea: media
      gráfica en blanco que solo dice «antes no existías».

      Los ceros de EN MEDIO se quedan, y ahí sí importan: un domingo cerrado
      tiene que verse como un valle. Y los del final también — «llevas cuatro
      días sin vender» es de las cosas que hay que ver.
    */
    const primero = todos.findIndex((p) => p.ingreso !== 0 || p.ganancia !== 0);
    const puntos = primero <= 0 ? todos : todos.slice(primero);

    const valores = puntos.flatMap((p) => [p.ingreso, p.ganancia]);
    // El techo nunca es cero: con todo en cero, dividir por el rango reventaría.
    const techo = Math.max(...valores, 0) || 1;
    const suelo = Math.min(...valores, 0);
    const rango = techo - suelo || 1;

    const x = (i: number) =>
      IZQ + (i * (ANCHO - IZQ - DER)) / Math.max(puntos.length - 1, 1);
    const y = (v: number) =>
      ARRIBA + ((techo - v) * (ALTO - ARRIBA - ABAJO)) / rango;

    return { puntos, techo, suelo, x, y };
  }, [dias]);

  const { puntos, techo, x, y } = calculado;

  /*
    ⚠️ NO SE DIBUJA SI NO HAY CON QUÉ COMPARAR.

    Con veintinueve días en cero y una punta al final, la gráfica no enseña una
    tendencia: enseña que hubo una venta. Ocupa media pantalla para decir algo
    que las cifras de arriba ya dijeron mejor.

    Tres días con movimiento es el mínimo donde una línea empieza a significar
    algo. Es el mismo criterio que «las últimas horas»: una barra sola no es un
    gráfico, es un adorno.
  */
  const diasConVenta = puntos.filter((p) => p.ingreso > 0).length;
  if (puntos.length < 2 || diasConVenta < 3) return null;

  const linea = (campo: "ingreso" | "ganancia") =>
    puntos.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p[campo])}`).join(" ");

  // La franja de en medio: baja por la facturación y vuelve por la utilidad.
  const franja =
    puntos.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.ingreso)}`).join(" ") +
    " " +
    puntos
      .map((_, i) => `L ${x(puntos.length - 1 - i)} ${y(puntos[puntos.length - 1 - i]!.ganancia)}`)
      .join(" ") +
    " Z";

  const totalIngreso = puntos.reduce((s, p) => s + p.ingreso, 0);
  const totalGanancia = puntos.reduce((s, p) => s + p.ganancia, 0);
  const margen = totalIngreso > 0 ? (totalGanancia / totalIngreso) * 100 : null;

  const mirado = encima === null ? null : puntos[encima];

  return (
    <Tarjeta>
      <TituloSeccion
        titulo="Utilidad vs facturación"
        descripcion="Lo que vendiste y lo que te quedó, día a día. La franja de en medio es lo que te costó."
        accion={
          margen !== null ? (
            <span className="flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success">
              <TrendingUp size={13} />
              {margen.toFixed(1).replace(".", ",")}% te queda
            </span>
          ) : undefined
        }
      />

      {/* La leyenda va ARRIBA del dibujo: leer los colores después de mirar la
          gráfica obliga a mirarla dos veces. */}
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5 text-muted">
          <span className="h-0.5 w-4 rounded-full bg-[var(--color-accent)]" />
          Facturado
        </span>
        <span className="flex items-center gap-1.5 text-muted">
          <span className="h-0.5 w-4 rounded-full bg-[var(--color-success)]" />
          Te quedó
        </span>

        {/* Lo que se está mirando ahora mismo, sin globo flotante: un tooltip
            que tapa la gráfica esconde justo lo que se quiere comparar. */}
        {mirado && (
          <span className="ml-auto flex items-center gap-2 tabular">
            <span className="text-muted">{fechaCorta(mirado.fecha)}</span>
            <span className="font-semibold text-accent">
              {formatMoney(mirado.ingreso.toFixed(4), formato)}
            </span>
            <span className="font-semibold text-success">
              {formatMoney(mirado.ganancia.toFixed(4), formato)}
            </span>
          </span>
        )}
      </div>

      <svg
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        className="w-full"
        style={{ height: 180 }}
        preserveAspectRatio="none"
        role="img"
        aria-label="Gráfica de lo facturado y lo que quedó, día a día"
        onMouseLeave={() => setEncima(null)}
      >
        <defs>
          <linearGradient id={idGradiente} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.04" />
          </linearGradient>
        </defs>

        {/* La línea del cero, solo si hay días en pérdida. Cuando todo es
            positivo coincide con el suelo y sería una raya de adorno. */}
        {calculado.suelo < 0 && (
          <line
            x1={IZQ}
            x2={ANCHO - DER}
            y1={y(0)}
            y2={y(0)}
            stroke="var(--color-line-strong)"
            strokeWidth="1"
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
          />
        )}

        <path d={franja} fill={`url(#${idGradiente})`} />

        <path
          d={linea("ingreso")}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={linea("ganancia")}
          fill="none"
          stroke="var(--color-success)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {mirado && encima !== null && (
          <>
            <line
              x1={x(encima)}
              x2={x(encima)}
              y1={ARRIBA}
              y2={ALTO - ABAJO}
              stroke="var(--color-line-strong)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <circle cx={x(encima)} cy={y(mirado.ingreso)} r="3.5" fill="var(--color-accent)" />
            <circle cx={x(encima)} cy={y(mirado.ganancia)} r="3.5" fill="var(--color-success)" />
          </>
        )}

        {/*
          Las zonas de ratón van al final para quedar por encima de todo lo
          dibujado. Son rectángulos invisibles de una columna cada uno: apuntar
          a una línea de 2 px es imposible, apuntar a una franja del ancho de un
          día es lo que la gente espera.
        */}
        {puntos.map((p, i) => (
          <rect
            key={p.fecha}
            x={x(i) - (ANCHO - IZQ - DER) / puntos.length / 2}
            y={0}
            width={(ANCHO - IZQ - DER) / puntos.length}
            height={ALTO}
            fill="transparent"
            onMouseEnter={() => setEncima(i)}
          />
        ))}
      </svg>

      <div className="mt-1 flex justify-between text-[11px] text-faint">
        <span>{fechaCorta(puntos[0]!.fecha)}</span>
        <span className="tabular">
          máx. {formatMoney(techo.toFixed(4), formato)}
        </span>
        <span>{fechaCorta(puntos[puntos.length - 1]!.fecha)}</span>
      </div>
    </Tarjeta>
  );
}

/** «28 ago» — el año sobra cuando el rango es de un mes. */
function fechaCorta(iso: string): string {
  return new Date(iso + "T12:00:00").toLocaleDateString("es-VE", {
    day: "numeric",
    month: "short",
  });
}

export { cx };
